import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Card, CardContent, Typography, Button, CircularProgress, Tabs, Tab, List, ListItem, ListItemText, ListItemIcon, Chip, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot, TimelineOppositeContent } from '@mui/lab';
import { Description as DescriptionIcon, PictureAsPdf as PdfIcon, Image as ImageIcon, Add as AddIcon, Remove as RemoveIcon, History as HistoryIcon } from '@mui/icons-material';
import * as projectsAPI from '../../services/projectsAPI';
import { Project, ProjectDocument, DailyReport, WorkReport } from '../../types/api';
import { AdvancedFileManager } from '../../components/AdvancedFileManager/AdvancedFileManager';
import ProjectDashboard from '../../components/ProjectDashboard/ProjectDashboard';

export const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);

  // Rollen-basierte Berechtigungen für Projekte
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure sehen nur eingeschränkte Daten
  const canSeeRestricted = isForeman || isInstaller;
  
  // Projekt-Berechtigungen:
  const canEditProjects = canSeeAll; // Admin, Management, Bauleiter können bearbeiten
  const canExportProjects = canSeeAll; // Nur Admin, Management, Bauleiter können exportieren
  const canSeeFinancialData = canSeeAll; // Auftragssumme/Materialanteil nur für Admin, Management, Bauleiter
  
  // Berichte States
  const [dailyReports, setDailyReports] = useState<DailyReport[]>([]);
  const [regieReports, setRegieReports] = useState<WorkReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  
  // Dashboard States
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [totalHours, setTotalHours] = useState(0);
  
  // Financial operation states
  const [increaseDialogOpen, setIncreaseDialogOpen] = useState(false);
  const [subtractDialogOpen, setSubtractDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [operationAmount, setOperationAmount] = useState('');
  const [operationDescription, setOperationDescription] = useState('');
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  
  // Contract history states
  const [contractHistory, setContractHistory] = useState<projectsAPI.ContractHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const calculateTotalHours = (reports: any[]) => {
    // Berechne Gesamtstunden aus allen Berichten
    let total = 0;
    reports.forEach(report => {
      if (report.mitarbeiter && Array.isArray(report.mitarbeiter)) {
        report.mitarbeiter.forEach((mitarbeiter: any) => {
          if (mitarbeiter.stunden && typeof mitarbeiter.stunden === 'number') {
            total += mitarbeiter.stunden;
          }
        });
      }
    });
    return total;
  };

  const fetchReports = async () => {
    if (!id) return;
    setReportsLoading(true);
    try {
      console.log('Fetching reports for project:', id);
      
      // Tagesberichte laden
      const dailyResponse = await fetch(`/api/daily-reports?project_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('Daily reports response status:', dailyResponse.status);
      
      if (dailyResponse.ok) {
        const dailyData = await dailyResponse.json();
        console.log('Daily reports data:', dailyData);
        // Extrahiere items aus der API-Antwort (paginierte Struktur)
        const dailyReports = dailyData.items || dailyData;
        console.log('Processed daily reports:', dailyReports);
        console.log('First daily report structure:', dailyReports[0]);
        setDailyReports(dailyReports);
      } else {
        console.error('Daily reports error:', dailyResponse.status, dailyResponse.statusText);
        const errorText = await dailyResponse.text();
        console.error('Error response:', errorText);
      }

      // Regieberichte laden - verwende die korrekte Regie-Reports-API
      const regieResponse = await fetch(`/api/regie-reports?project_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('Work reports response status:', regieResponse.status);
      
      if (regieResponse.ok) {
        const regieData = await regieResponse.json();
        console.log('Work reports data:', regieData);
        // Regieberichte-API gibt direktes Array zurück (nicht paginiert)
        const workReports = Array.isArray(regieData) ? regieData : (regieData.items || []);
        console.log('Processed work reports:', workReports);
        setRegieReports(workReports);
      } else {
        console.error('Work reports error:', regieResponse.status, regieResponse.statusText);
        const errorText = await regieResponse.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    const fetchProject = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await projectsAPI.getProject(Number(id));
        setProject(data);
        setError(null);
      } catch (e) {
        setError('Projekt konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  // Lade Berichte für Dashboard und wenn Tab gewechselt wird
  useEffect(() => {
    fetchReports();
  }, [id]);

  // Lade Dokumente für Dashboard
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!id) return;
      try {
        const response = await fetch(`/api/projects/${id}/documents`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setDocuments(data.items || data || []);
        }
      } catch (error) {
        console.error('Error loading documents:', error);
        setDocuments([]);
      }
    };
    
    fetchDocuments();
  }, [id]);

  // Berechne Gesamtstunden wenn sich Berichte ändern
  useEffect(() => {
    if (dailyReports.length > 0 || regieReports.length > 0) {
      const allReports = [...dailyReports, ...regieReports];
      const totalHours = calculateTotalHours(allReports);
      setTotalHours(totalHours);
    }
  }, [dailyReports, regieReports]);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!project) return <Typography>Kein Projekt gefunden.</Typography>;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleFolderChange = (folderId: number | null) => {
    setCurrentFolderId(folderId);
  };

  const handleDocumentSelect = (document: ProjectDocument) => {
    // Hier können Sie die Dokument-Auswahl behandeln
    console.log('Selected document:', document);
  };

  // Financial operations handlers
  const handleIncreaseContractAmount = async () => {
    if (!project || !operationAmount || !operationDescription) return;
    
    const amount = parseFloat(operationAmount);
    if (isNaN(amount) || amount <= 0) {
      setOperationError('Bitte geben Sie einen gültigen positiven Betrag ein.');
      return;
    }

    setOperationLoading(true);
    setOperationError(null);
    
    try {
      await projectsAPI.increaseContractAmount(project.id, amount, operationDescription);
      
      // Refresh project data
      const updatedProject = await projectsAPI.getProject(project.id);
      setProject(updatedProject);
      
      // Close dialog and reset form
      setIncreaseDialogOpen(false);
      setOperationAmount('');
      setOperationDescription('');
      
      alert(`Auftragssumme erfolgreich um ${amount}€ erhöht.`);
    } catch (error: any) {
      console.error('Error increasing contract amount:', error);
      setOperationError(error.response?.data?.detail || 'Fehler beim Erhöhen der Auftragssumme');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleSubtractInvoicedAmount = async () => {
    if (!project || !operationAmount || !operationDescription) return;
    
    const amount = parseFloat(operationAmount);
    if (isNaN(amount) || amount <= 0) {
      setOperationError('Bitte geben Sie einen gültigen positiven Betrag ein.');
      return;
    }

    setOperationLoading(true);
    setOperationError(null);
    
    try {
      await projectsAPI.subtractInvoicedAmount(project.id, amount, operationDescription);
      
      // Refresh project data
      const updatedProject = await projectsAPI.getProject(project.id);
      setProject(updatedProject);
      
      // Close dialog and reset form
      setSubtractDialogOpen(false);
      setOperationAmount('');
      setOperationDescription('');
      
      alert(`${amount}€ erfolgreich von der Auftragssumme abgezogen.`);
    } catch (error: any) {
      console.error('Error subtracting invoiced amount:', error);
      setOperationError(error.response?.data?.detail || 'Fehler beim Abziehen des Betrags');
    } finally {
      setOperationLoading(false);
    }
  };

  const resetOperationDialog = () => {
    setOperationAmount('');
    setOperationDescription('');
    setOperationError(null);
  };

  const loadContractHistory = async () => {
    if (!project) return;
    
    setHistoryLoading(true);
    try {
      const history = await projectsAPI.getContractHistory(project.id);
      setContractHistory(history);
    } catch (error) {
      console.error('Error loading contract history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryClick = () => {
    setHistoryDialogOpen(true);
    loadContractHistory();
  };

  return (
    <Box mt={4}>
      <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Zurück
      </Button>
      
      {/* Projekt-Informationen */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {project.name}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                {project.description}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canEditProjects && (
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/projects/${project.id}/edit`)}
                >
                  Bearbeiten
                </Button>
              )}
              {canExportProjects && (
                <Button
                  variant="contained"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`/api/projects/${project.id}/generate-report`, {
                        headers: {
                          'Authorization': `Bearer ${token}`,
                        },
                      });
                      
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `Projektbericht_${project.name}_${new Date().toISOString().split('T')[0]}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } else {
                        console.error('Export failed:', response.status, response.statusText);
                        alert('Fehler beim Export: ' + response.statusText);
                      }
                    } catch (error) {
                      console.error('Export error:', error);
                      alert('Fehler beim Export');
                    }
                  }}
                >
                  Export
                </Button>
              )}
            </Box>
          </Box>
          <Typography><strong>Kunde:</strong> {project.client_name}</Typography>
          <Typography><strong>Ansprechpartner:</strong> {project.client_contact}</Typography>
          <Typography><strong>Adresse:</strong> {project.site_address}</Typography>
          <Typography><strong>Status:</strong> {project.status}</Typography>
          <Typography><strong>Startdatum:</strong> {project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'}</Typography>
          <Typography><strong>Enddatum:</strong> {project.end_date ? new Date(project.end_date).toLocaleDateString() : '-'}</Typography>
          {canSeeFinancialData && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, mb: 1 }}>
                <Typography><strong>Auftragssumme:</strong> {project.contract_amount?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) || '0,00 €'}</Typography>
                {(isAdmin || isBauleiter) && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        resetOperationDialog();
                        setIncreaseDialogOpen(true);
                      }}
                      color="success"
                    >
                      Nachtrag
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RemoveIcon />}
                      onClick={() => {
                        resetOperationDialog();
                        setSubtractDialogOpen(true);
                      }}
                      color="warning"
                    >
                      Abrechnung
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<HistoryIcon />}
                      onClick={handleHistoryClick}
                      color="info"
                    >
                      Änderungshistorie
                    </Button>
                  </Box>
                )}
              </Box>
              <Typography><strong>Materialanteil:</strong> {project.material_share?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) || '0,00 €'}</Typography>
            </>
          )}
          <Typography><strong>Geplante Stunden:</strong> {project.planned_hours}</Typography>
        </CardContent>
      </Card>

      {/* Tabs für verschiedene Bereiche */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="project tabs">
            <Tab label="Übersicht" />
            <Tab label="Dokumente" />
            <Tab label="Tagesberichte" />
            <Tab label="Regieberichte" />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <ProjectDashboard
              project={project}
              dailyReports={dailyReports}
              regieReports={regieReports}
              documents={documents}
              totalHours={totalHours}
              isLoading={reportsLoading}
              onTabChange={(tabIndex) => setActiveTab(tabIndex)}
            />
          )}
          
          {activeTab === 1 && (
                          <AdvancedFileManager
                projectId={Number(id)}
                currentFolderId={currentFolderId || undefined}
                onFolderChange={handleFolderChange}
                onDocumentSelect={handleDocumentSelect}
              />
          )}
          
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Tagesberichte
              </Typography>
              {reportsLoading ? (
                <CircularProgress />
              ) : dailyReports.length > 0 ? (
                <List>
                  {dailyReports.map((report) => (
                    <ListItem key={report.id} divider>
                      <ListItemIcon>
                        <DescriptionIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Bericht #${report.id} - ${new Date(report.date).toLocaleDateString('de-DE')}`}
                        secondary={`Projekt: ${report.project_name || 'Unbekanntes Projekt'}`}
                      />
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => navigate(`/tagesberichte/${report.id}`)}
                      >
                        Öffnen
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography>Keine Tagesberichte gefunden.</Typography>
              )}
            </Box>
          )}
          
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Regieberichte
              </Typography>
              {reportsLoading ? (
                <CircularProgress />
              ) : regieReports.length > 0 ? (
                <List>
                  {regieReports.map((report) => (
                    <ListItem key={report.id} divider>
                      <ListItemIcon>
                        <DescriptionIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Bericht #${report.id} - ${new Date(report.date).toLocaleDateString('de-DE')}`}
                        secondary={`Material: ${report.materials_used || 'Keine Materialdaten'}`}
                      />
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => navigate(`/regie-reports/${report.id}`)}
                      >
                        Öffnen
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography>Keine Regieberichte gefunden.</Typography>
              )}
            </Box>
          )}
        </Box>
      </Card>

      {/* Dialog für Auftragssumme erhöhen (Nachträge) */}
      <Dialog 
        open={increaseDialogOpen} 
        onClose={() => {
          setIncreaseDialogOpen(false);
          resetOperationDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Auftragssumme erhöhen (Nachtrag)</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {operationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {operationError}
              </Alert>
            )}
            <TextField
              label="Betrag (€)"
              type="number"
              value={operationAmount}
              onChange={(e) => setOperationAmount(e.target.value)}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
              disabled={operationLoading}
            />
            <TextField
              label="Beschreibung/Grund"
              value={operationDescription}
              onChange={(e) => setOperationDescription(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              placeholder="z.B. Zusätzliche Arbeiten, Materialnachtrag, etc."
              disabled={operationLoading}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Aktuelle Auftragssumme: {project?.contract_amount?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) || '0,00 €'}
            </Typography>
            {operationAmount && !isNaN(parseFloat(operationAmount)) && (
              <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                Neue Auftragssumme: {((project?.contract_amount || 0) + parseFloat(operationAmount)).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setIncreaseDialogOpen(false);
              resetOperationDialog();
            }}
            disabled={operationLoading}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleIncreaseContractAmount}
            variant="contained"
            color="success"
            disabled={operationLoading || !operationAmount || !operationDescription}
            startIcon={operationLoading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {operationLoading ? 'Wird erhöht...' : 'Erhöhen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog für Abgerechnete Beträge abziehen */}
      <Dialog 
        open={subtractDialogOpen} 
        onClose={() => {
          setSubtractDialogOpen(false);
          resetOperationDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Abgerechneten Betrag abziehen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {operationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {operationError}
              </Alert>
            )}
            <TextField
              label="Betrag (€)"
              type="number"
              value={operationAmount}
              onChange={(e) => setOperationAmount(e.target.value)}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
              disabled={operationLoading}
            />
            <TextField
              label="Beschreibung/Grund"
              value={operationDescription}
              onChange={(e) => setOperationDescription(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              placeholder="z.B. Rechnung Nr. 2024-001, Abschlagsrechnung, etc."
              disabled={operationLoading}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Aktuelle Auftragssumme: {project?.contract_amount?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) || '0,00 €'}
            </Typography>
            {operationAmount && !isNaN(parseFloat(operationAmount)) && (
              <Typography 
                variant="body2" 
                color={((project?.contract_amount || 0) - parseFloat(operationAmount)) >= 0 ? "warning.main" : "error.main"} 
                sx={{ mt: 0.5 }}
              >
                Neue Auftragssumme: {((project?.contract_amount || 0) - parseFloat(operationAmount)).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setSubtractDialogOpen(false);
              resetOperationDialog();
            }}
            disabled={operationLoading}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleSubtractInvoicedAmount}
            variant="contained"
            color="warning"
            disabled={operationLoading || !operationAmount || !operationDescription}
            startIcon={operationLoading ? <CircularProgress size={20} /> : <RemoveIcon />}
          >
            {operationLoading ? 'Wird abgezogen...' : 'Abziehen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog für Änderungshistorie */}
      <Dialog 
        open={historyDialogOpen} 
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Änderungshistorie der Auftragssumme</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : contractHistory.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              Keine Änderungen gefunden.
            </Typography>
          ) : (
            <Timeline position="alternate">
              {contractHistory.map((entry, index) => (
                <TimelineItem key={entry.id}>
                  <TimelineOppositeContent
                    sx={{ m: 'auto 0' }}
                    align={index % 2 === 0 ? "right" : "left"}
                    variant="body2"
                    color="text.secondary"
                  >
                    {new Date(entry.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    <br />
                    <Typography variant="caption">
                      {entry.created_by_name}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineConnector />
                                         <TimelineDot 
                       color={
                         entry.transaction_type === 'initial' ? 'primary' :
                         entry.transaction_type === 'increase' ? 'success' : 'warning'
                       }
                       sx={{ 
                         width: 40, 
                         height: 40, 
                         display: 'flex', 
                         alignItems: 'center', 
                         justifyContent: 'center',
                         fontSize: '16px',
                         fontWeight: 'bold'
                       }}
                     >
                       {entry.transaction_type === 'initial' ? '€' :
                        entry.transaction_type === 'increase' ? '+' : '-'}
                     </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Typography variant="h6" component="span">
                      {entry.transaction_type === 'initial' ? 'Ursprüngliche Auftragssumme' :
                       entry.transaction_type === 'increase' ? 'Nachtrag' : 'Abrechnung'}
                    </Typography>
                    <Typography color="text.secondary">
                      {entry.description}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Betrag:</strong> {entry.amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Von:</strong> {entry.previous_amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} 
                        <strong> → Auf:</strong> {entry.new_amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetail; 